import "./ApPaymentHome.scss";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TablePagination,
} from "@mui/material";
import CircularButton, { CustomButtonMenu, CustomLoadingButton } from "../../../components/ui/CustomButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import newRequest from "../../../utils/newRequest";
import { motion } from "framer-motion";
import { GridLoader, ScaleLoader } from "react-spinners";
import numeral from "numeral";
import moment from "moment";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { MdAdd, MdArrowBackIos, MdClose, MdPayments } from "react-icons/md";
import PageviewOutlinedIcon from '@mui/icons-material/PageviewOutlined';
import { IoClose, IoDocumentTextSharp } from "react-icons/io5";
import VideoPlayer from "../../../components/videoPlayer/VideoPlayer";
import WorkedOnBy from "../../../components/workedOnBy/WorkedOnBy";
import { useTranslation } from "react-i18next";
import OnScreenReports from "../../../components/onScreenReports/OnScreenReports";
import ExpandableComponent from "../../../components/expandableComponent/ExpandableComponent";
import { fetchClientsBySearch } from "../../../utils/genericFunctions";
import { customSelectStyles_dark, customSelectStyles_light } from "../../../components/ui/CustomSelect";
import ExportToExcelButton from "../../../components/exportToExcelButton/ExportToExcelButton";
import InitialsAvatar from "../../../components/InitialsAvatar/InitialsAvatar";

const ApPaymentHome = () => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const { currentTheme } = useSelector((state) => state.theme);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const navigate = useNavigate();
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [payments, setPayments] = useState([]);
  const location = useLocation();
  const showBack = location.state?.showBack || "N";

  const [disableOtherFields, setDisableOtherFields] = useState(false);
  const [filterFmDate, setFilterFmDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());
  const [filterHdNo, setFilterHdNo] = useState(null);
  const [filterClient, setFilterClient] = useState(null);
  const [filterBankAccount, setFilterBankAccount] = useState(null);
  const [filterBranch, setFilterBranch] = useState(null);
  const [filterCurrency, setFilterCurrency] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);

  const [bankAccounts, setBankAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [filterPayingFor, setFilterPayingFor] = useState(null);
  const [payingFor, setPayingFor] = useState([]);

  const fetchPayments = async () => {
    try {
      setIsLoading(true);
      // Simulate delay for 5 seconds
      // await new Promise((resolve) => setTimeout(resolve, 5000));
      const res = await newRequest.post(
        "/apPaymentHdr/findAll",
        {
          hd_mode: "",
          hd_status: "",
          hd_paying_for: "",
          result_limit: 5
        }
      );
      setPayments(res.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const res = await newRequest.get("/glBankAc/findAll");
      const filteredAccounts = res.data.filter((bank) =>
        bank?.ac_status === "Active" &&
        bank?.ac_visible === "Y" &&
        bank?.ac_pay === "Y"
      );
      const options = filteredAccounts.map((bank) => ({
        bank_code: bank?.bank_code_xx,
        branch_code: bank?.bank_branch_code_xx,
        value: bank?.ac_no,
        label: bank?.ac_name,
      }));
      setBankAccounts(options);
    } catch (error) {
      console.error("Error fetching bank accounts :", error);
    }
  };

  const fetchPayingFor = async () => {
    try {
      const res = await newRequest.post("/sysCode/findAll", { sys_type: "Paying For" });
      const options = res.data.map((mode) => ({
        value: mode?.sys_code,
        label: mode?.sys_name,
      }));
      setPayingFor(options);
    } catch (error) {
      console.error("Error fetching payment modes :", error);
    }
  };


  const fetchBranches = async () => {
    try {
      const res = await newRequest.post("/orgUnit/findAll", { unit_status: "Active" });
      const options = res.data.map((branch) => ({
        value: branch?.unit_code,
        label: branch?.unit_name,
      }));
      setBranches(options);
    } catch (error) {
      console.error("Error fetching entity types:", error);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const res = await newRequest.get("/glCurrency/findAll");
      let options = res.data.map((currency) => ({
        value: currency?.cur_code,
        label: currency?.cur_name,
      }));
      options.unshift({ value: "All", label: "All" });
      setCurrencies(options);
      setFilterCurrency(options[0]);
    } catch (error) {
      console.error("Error fetching entity types:", error);
    }
  };

  const fetchStatuses = async () => {
    try {
      const options = ["All", "New", "Validated", "Approved", "Cancelled", "In-Approval"].map((mode) => ({
        value: mode,
        label: mode,
      }));
      setStatuses(options);
      setFilterStatus({
        value: "All",
        label: "All",
      });
    } catch (error) {
      console.error("Error fetching payment modes :", error);
    }
  };

  const setDates = () => {
    try {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();

      // Beginning of the year
      const beginningOfYear = new Date(currentYear, 0, 1);
      beginningOfYear.setFullYear(currentYear); // Set full year again to handle leap years
      const formattedBeginningOfYear = beginningOfYear
        .toISOString()
        .split("T")[0];

      // End of the year
      const endOfYear = new Date(currentYear + 1, 0, 0);
      const formattedEndOfYear = endOfYear.toISOString().split("T")[0];

      // Beginning of the month
      const beginningOfMonth = new Date(currentYear, currentMonth, 1);
      // console.log(`Begining of the month ${beginningOfMonth}`)
      const formattedBeginningOfMonth = beginningOfMonth
        .toISOString()
        .split("T")[0];

      // End of the month
      const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
      endOfMonth.setDate(endOfMonth.getDate() + 1); // Add one day to get the actual end of the month
      const formattedEndOfMonth = endOfMonth.toISOString().split("T")[0];

      // Formatting current date and one year later
      const formattedCurrentDate = currentDate.toISOString().split("T")[0];
      const oneYearLater = new Date(currentDate);
      oneYearLater.setFullYear(currentYear + 1);
      const formattedOneYearLater = oneYearLater.toISOString().split("T")[0];

      // Set these values to your state or do whatever you want with them
      setFilterFmDate(formattedBeginningOfMonth);
      setFilterToDate(formattedEndOfMonth);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOpenOverlay = () => {
    setIsOverlayOpen(true);
  };



  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 5));
    setPage(0);
  };


  const filteredData = payments?.filter(
    (item) =>
      item.hd_no?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      item.bank_ac_no_xx?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      item.bank_ac_name_xx?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      item.account_name_xx?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      item.hd_chq_no?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      item.hd_fc_amount
        ?.toString()
        .toLowerCase()
        .includes(searchTerm?.toLowerCase()) ||
      item.hd_ref_no?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const slicedPaymentData = filteredData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSearchPayments = async () => {
    try {
      setIsLoading(true);
      if (searchTerm) {
        const res = await newRequest.post("/apPaymentHdr/search", { search_input: searchTerm });
        setPayments(res.data);
      } else {
        fetchPayments();
      }
    } catch (error) {
      console.error("Error searching data:", error);
    } finally {
      setIsLoading(false);
    }
  };


  const menus = [
    {
      _id: "home",
      title: t('home'),
    },
    {
      _id: "list",
      title: t('list'),
    },
  ];

  const handleGetPaymentListing = async () => {
    try {
      // await new Promise((resolve) => setTimeout(resolve, 5000));
      setIsProcessing(true);
      const res = await newRequest.post("/apPaymentHdr/paymentsListing", {
        hd_no: filterHdNo,
        date_fm: filterFmDate,
        date_to: filterToDate,
        hd_biz_unit: !filterBranch || filterBranch?.value === "All" ? "" : filterBranch?.value,
        hd_ac_no: !filterBankAccount || filterBankAccount.value === "All" ? "" : filterBankAccount?.value,
        hd_status: !filterStatus || filterStatus.value === "All" ? "" : filterStatus?.value,
        hd_paying_for: !filterPayingFor || filterPayingFor.value === "All" ? "" : filterPayingFor?.value,
        hd_cur_code: !filterCurrency || filterCurrency?.value === "All" ? "" : filterCurrency?.value,
        hd_ent_code: !filterClient || filterClient?.value === "All" ? "" : filterClient?.value,
      });
      console.log(`paymentsListing  ${JSON.stringify(res.data, null, 2)}`);
      if (res.data?.list) {
        setPayments(res.data?.list);
      } else {
        setPayments([]);
      }
      setIsProcessing(false);
    } catch (error) {
      console.log(error);
      setIsProcessing(false);
    }
  };

  const exportConfig = [
    { key: "hd_no", label: "Payment No.", format: "s" },
    { key: "hd_type", label: "Type", format: "s" },
    { key: "hd_gl_date", label: "GL Date", format: "d" },
    { key: "hd_status", label: "Status", format: "s" },
    { key: "hd_narration", label: "Narration", format: "s" },
    { key: "hd_mode", label: "Mode", format: "s" },
    { key: "hd_ref_no", label: "Ref No", format: "s" },
    { key: "hd_paying_for", label: "Purpose", format: "s" },
    { key: "bizunit_name_xx", label: "Branch Name", format: "s" },
    { key: "hd_biz_unit", label: "Branch Code", format: "s" },
    { key: "bank_ac_no_xx", label: "Account From No", format: "s" },
    { key: "bank_ac_name_xx", label: "Account From Name", format: "s" },
    { key: "hd_chq_no", label: "Cheque No", format: "s" },
    { key: "hd_chq_date", label: "Cheque Date", format: "s" },
    { key: "client_name_xx", label: "Client Name", format: "s" },
    { key: "client_code_xx", label: "Client Code", format: "s" },
    { key: "hd_cur_code", label: "Currency", format: "s" },
    { key: "hd_cur_rate", label: "Rate", format: "n" },
    { key: "hd_fc_amount", label: "Amount FC", format: "n" },
    { key: "hd_lc_amount", label: "Amount LC", format: "n" },
    { key: "allocated_fc_amount_xx", label: "Allocated FC", format: "n" },
    { key: "allocated_lc_amount_xx", label: "Allocated LC", format: "n" },
  ];

  useEffect(() => {
    fetchPayments();

    fetchBankAccounts();
    fetchPayingFor();
    fetchBranches();
    fetchCurrencies();
    fetchStatuses();
    setDates();

    setSelectedMenu(menus[0]);
  }, []);

  return (
    <motion.div
      className="ap-payment-home"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 1 } }}
      exit={{ opacity: 0, transition: { duration: 1 } }}
    >
      {isLoading ? (
        <div className="no-content">
          <ScaleLoader
            color="var(--primary)"
            height={50}
            radius={8}
            width={4}
          />
        </div>
      ) : (
        <div className="ap-payment-home-main-wrapper">
          <motion.div className="list-wrapper"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}>
            <div className="list-navbar">
              <div className="list-navbar-left">
                <div className="apHeroIcon"><MdPayments /></div>
                <div className="apHeroTitle">{t("payments")}</div>
              </div>
              <div className="list-navbar-right">
                <div className="home-menus-wrapper">
                  {menus?.map((row, index) => (
                    <React.Fragment key={row._id}>
                      <span style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
                        <CustomButtonMenu
                          className="single-menu"
                          currentButton={selectedMenu?._id === row?._id}
                          currentTheme={currentTheme}
                          onClick={async () => {
                            try {
                              setSelectedMenu(row);
                              if (row?._id === "list") {
                                await fetchBankAccounts();
                                await fetchPayingFor();
                                await fetchBranches();
                                await fetchCurrencies();
                                await fetchStatuses();
                                setDates();
                              }
                            } catch (error) {
                              console.log(error);
                            }
                          }}
                        >
                          {row?.title}
                        </CustomButtonMenu>
                      </span>
                      {index < menus.length - 1 && <span className="separator-v" />}
                    </React.Fragment>
                  ))}
                </div>
                <span className="separator-v" />
                <CustomLoadingButton
                  currentTheme={currentTheme}
                  iconPosition="end"
                  borderRadiusType="both"
                  variant="tint"
                  color="#2563EB"
                  tooltip="Run receipts reports"
                  endIcon={<IoDocumentTextSharp />}
                  onClick={() => navigate("/reports/ap-payables", { state: { showBack: "Y" } })}>
                  {t('reports')}
                </CustomLoadingButton>
                <CustomLoadingButton
                  currentTheme={currentTheme}
                  iconPosition="end"
                  borderRadiusType="both"
                  variant="tint"
                  color="#0f766e5d"
                  endIcon={<RefreshIcon />} onClick={fetchPayments}>
                  {t('refresh')}
                </CustomLoadingButton>
                <CustomLoadingButton
                  currentTheme={currentTheme}
                  iconPosition="end"
                  borderRadiusType="both"
                  variant="tint"
                  color="#0ea5a4"
                  tooltip={"New payment"}
                  endIcon={<MdAdd />}
                  onClick={() => navigate(`/payment/new`)}
                >
                  {t('newPayment')}
                </CustomLoadingButton>
                {/* This part shows if the menu was opened from home screen */}
                {showBack === "Y" && <hr className="separator-v" />}
                {showBack === "Y" &&
                  <span className="back-icon" onClick={() => navigate(-1)}>
                    <MdArrowBackIos size={24} />
                  </span>}
              </div>
            </div>
            {selectedMenu?._id === "home" && (
              <div className="apHome">
                {/* HERO */}
                <div className="apHero">

                  <div className="apHeroSub">
                    {t("accountsPayablesDesc")}
                  </div>

                  {/* Search */}
                  <div className="homeSearch">
                    <FiSearch className="homeSearchIcon" />
                    <input
                      className="homeSearchInput"
                      type="text"
                      placeholder={`${t('search')} ${t('payments')}`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearchPayments();
                      }}
                    />
                    {searchTerm?.length > 0 ? (
                      <CloseIcon
                        className="homeSearchClear"
                        onClick={() => {
                          try {
                            fetchPayments();
                            setSearchTerm("");
                          } catch (error) {
                            console.log(error);
                          }
                        }}
                      />
                    ) : (
                      <span className="homeSearchClearPlaceholder" />
                    )}
                  </div>
                </div>

                {/* GRID */}
                <div className="apCardsGrid">
                  {slicedPaymentData?.map((payment) => {
                    const status = (payment?.hd_status || "").toUpperCase();
                    const isNew = status === "NEW";
                    const isApproved = status === "APPROVED";
                    const isCancelled = status === "CANCELLED";
                    const isValidated = status === "VALIDATED";

                    const amount = numeral(payment?.hd_fc_amount || 0).format("0,0.00");
                    const allocated = numeral(payment?.allocated_fc_amount_xx || 0).format("0,0.00");
                    const isFullyAllocated =
                      Number(payment?.allocated_fc_amount_xx || 0) >= Number(payment?.hd_fc_amount || 0) &&
                      Number(payment?.hd_fc_amount || 0) > 0;

                    return (
                      <div
                        key={payment?._id}
                        className={`apCard ${isNew ? "isNew" : ""} ${isApproved ? "isApproved" : ""} ${isCancelled ? "isCancelled" : ""}`}
                        onClick={() => navigate(`/payment/${payment._id}`)}
                      >
                        {/* top */}
                        <div className="apCardTop">
                          <div className="apCardBadge">
                            <MdPayments />
                          </div>

                          <div className="apCardTopMeta">
                            <div className="apCardNo">{payment?.hd_no}</div>
                            <div className="apCardName">{payment?.hd_type === "Normal" ? payment?.client_details_xx?.ent_name : payment?.hd_misc_name}</div>
                            <div className="apCardTiny">{payment?.hd_type === "Normal" ? payment?.client_details_xx?.aent_name_xx : payment?.hd_type}</div>
                          </div>

                          <div className="apCardTopRight">
                            <span className={`apStatusPill ${status}`}>
                              {status || "STATUS"}
                            </span>
                          </div>
                        </div>

                        {/* divider */}
                        <div className="apDivider" />

                        {/* amount block */}
                        <div className="apAmountRow">
                          <div className="apAmountLabel">
                            <div className="apAmountTitle">{`Amount [${payment?.hd_cur_code || ""}]`}</div>
                          </div>

                          <div className="apAmountValueWrap">
                            <div className={`apAmountValue ${isCancelled ? "neg" : ""}`}>
                              {amount}
                            </div>
                            <div className={`apAmountHint ${isFullyAllocated ? "ok" : ""}`}>
                              {isCancelled ? "VOIDED" : isFullyAllocated ? t('fullyAllocated').toUpperCase() : t('received').toUpperCase()}
                            </div>
                          </div>
                        </div>

                        {/* meta row */}
                        <div className="apMetaRow">
                          <div className="apMetaCol" style={{ flex: "1.5" }}>
                            <div className="apMetaLabel">{t('paymentMode').toUpperCase()}</div>
                            <div className="apMetaValue">{payment?.hd_mode || "-"}</div>
                          </div>

                          <div className="apMetaCol" style={{ flex: "1", textAlign: "right" }}>
                            <div className="apMetaLabel">{t('date').toUpperCase()}</div>
                            <div className="apMetaValue">
                              {payment?.hd_gl_date ? moment(payment?.hd_gl_date).format("DD-MMM-YYYY") : "-"}
                            </div>
                          </div>
                        </div>

                        {/* footer */}
                        <div className="apCardFooter">
                          <div className="apFooterLeft">
                            {/* <div className="apOrgDot" /> */}
                            <InitialsAvatar
                              name={payment?.updated_by_name_xx}
                              size={20}
                              currentTheme={currentTheme}
                            />
                            <div className="apOrgName">{payment?.updated_by_name_xx || "Bima Go"}</div>
                          </div>

                          <div className="apFooterRight">
                            <div className="apFooterTiny">{payment?.time_ago_xx || ""}</div>
                            <div className="apFooterTiny">{`Allocated ${allocated}`}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Create New Receipt Tile */}
                  <div
                    className="apCardCreate"
                    onClick={() => navigate(`/payment/new`)}
                  >
                    <div className="apCreatePlus">+</div>
                    <div className="apCreateText">{t('createNewPayment')}</div>
                  </div>
                </div>

                {/* Floating help button (keeps your existing help overlay logic) */}
                {/* <div className="apFloatingHelp">
                  <button className="overlay-button" onClick={handleOpenOverlay}>
                    ?
                  </button>
                </div> */}
              </div>
            )}
            {selectedMenu?._id === "list" &&
              <motion.div
                className="filter-wrapper"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 1 } }}
                exit={{ opacity: 0, transition: { duration: 1 } }}
              >
                <ExpandableComponent
                  title={<h3 className="normal-title" style={{ alignSelf: "center" }}>{t('pleaseEnterSearch')}</h3>}
                  subtitle={""}
                  defaultExpanded={true}
                >
                  <div className="content-wrapper-selection">
                    <div className="filter">
                      <div className="left">
                        <div className="input-item-with-label">
                          <label
                            htmlFor="fm_date"
                            className="form-dropdown-label"
                            style={{ width: "100px" }}
                          >
                            {t('from').toUpperCase()}
                          </label>
                          <input
                            id="fm_date"
                            name="fm_date"
                            type="date"
                            placeholder={t('from').toUpperCase()}
                            value={filterFmDate}
                            className="form-input-item"
                            style={{ width: "150px" }}
                            onChange={(e) => {
                              if (e.target.value) {
                                const formattedFmDate = moment(e.target.value).format("YYYY-MM-DD");
                                setFilterFmDate(formattedFmDate);
                              }
                            }}
                            disabled={disableOtherFields}
                          />
                        </div>
                        <div className="input-item-with-label">
                          <label
                            htmlFor="to_date"
                            className="form-dropdown-label"
                            style={{ width: "100px" }}
                          >
                            {t('to').toUpperCase()}
                          </label>
                          <input
                            id="to_date"
                            name="to_date"
                            type="date"
                            placeholder={t('to').toUpperCase()}
                            value={filterToDate}
                            className="form-input-item"
                            style={{ width: "150px" }}
                            onChange={(e) => {
                              if (e.target.value) {
                                const formattedToDate = moment(e.target.value).format("YYYY-MM-DD");
                                setFilterToDate(formattedToDate);
                              }
                            }}
                            disabled={disableOtherFields}
                          />
                        </div>
                        <div className="input-item-with-label">
                          <label
                            htmlFor="filter_hd_no"
                            className="form-dropdown-label"
                            style={{ width: "100px" }}
                          >
                            {t('paymentNo').toUpperCase()}
                          </label>
                          <input
                            id="filter_hd_no"
                            name="filter_hd_no"
                            type="text"
                            placeholder={t('paymentNo').toUpperCase()}
                            value={filterHdNo}
                            className="form-input-item"
                            style={{ width: "150px" }}
                            onChange={(e) => {
                              try {
                                setFilterHdNo(e.target.value);
                                if (e.target.value) {
                                  setDisableOtherFields(true);
                                } else {
                                  setDisableOtherFields(false);
                                }
                              } catch (error) {
                                console.log(error);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleGetPaymentListing();
                            }}
                          />
                        </div>
                      </div>
                      <div className="center">
                        <div className="input-item-with-label">
                          <label htmlFor="product_code" style={{ width: "130px" }}>
                            {t('source').toUpperCase()}
                          </label>
                          <Select
                            id="hd_ac_no"
                            name="hd_ac_no"
                            menuPortalTarget={document.body}
                            styles={{
                              ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999,
                              })
                            }}
                            width="350px"
                            value={filterBankAccount}
                            onChange={(selectedOption) => setFilterBankAccount(selectedOption)}
                            options={bankAccounts}
                            isSearchable={true}
                            isClearable
                            placeholder={t('source').toUpperCase()}
                            isDisabled={disableOtherFields}
                          />
                        </div>
                        <div className="input-item-with-label">
                          <label htmlFor="paying_for" style={{ width: "130px" }}>
                            {t('purpose').toUpperCase()}
                          </label>
                          <Select
                            id="paying_for"
                            name="paying_for"
                            menuPortalTarget={document.body}
                            styles={{
                              ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999,
                              })
                            }}
                            width="350px"
                            value={filterPayingFor}
                            onChange={(selectedOption) => setFilterPayingFor(selectedOption)}
                            options={payingFor}
                            isSearchable={true}
                            isClearable
                            placeholder={t('purpose').toUpperCase()}
                            isDisabled={disableOtherFields}
                          />
                        </div>
                        <div className="input-item-with-label">
                          <label htmlFor="client" style={{ width: "130px" }}>
                            {t('client').toUpperCase()}
                          </label>
                          <AsyncSelect
                            id="client"
                            name="client"
                            menuPortalTarget={document.body}
                            styles={{
                              ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999,
                              })
                            }}
                            width="350px"
                            value={filterClient}
                            onChange={(selectedOption) => setFilterClient(selectedOption)}
                            loadOptions={fetchClientsBySearch}
                            isSearchable={true}
                            isClearable
                            placeholder={t('client').toUpperCase()}
                            noOptionsMessage={() => "Type Minimum 2 Letter to Search"}
                            isDisabled={disableOtherFields}
                          />
                        </div>
                      </div>
                      <div className="right">
                        <div className="input-item-with-label biz_unit">
                          <label htmlFor="biz_unit" style={{ width: "100px" }}>
                            {t('bizUnit').toUpperCase()}
                          </label>
                          <Select
                            id="biz_unit"
                            name="biz_unit"
                            styles={{
                              ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999,
                              })
                            }}
                            width="150px"
                            value={filterBranch}
                            onChange={(selectedOption) => setFilterBranch(selectedOption)}
                            options={branches}
                            isSearchable={true}
                            placeholder={t('bizUnit').toUpperCase()}
                            isDisabled={disableOtherFields}
                          />
                        </div>
                        <div className="input-item-with-label">
                          <label htmlFor="currency_code" style={{ width: "100px" }}>
                            {t('currency').toUpperCase()}
                          </label>
                          <Select
                            id="currency_code"
                            name="currency_code"
                            menuPortalTarget={document.body}
                            styles={{
                              ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999,
                              })
                            }}
                            width="150px"
                            value={filterCurrency}
                            onChange={(selectedOption) => setFilterCurrency(selectedOption)}
                            options={currencies}
                            isSearchable={true}
                            placeholder={t('currency').toUpperCase()}
                            isDisabled={disableOtherFields}
                          />
                        </div>
                        <div className="input-item-with-label">
                          <label htmlFor="status" style={{ width: "100px" }}>
                            {t('status').toUpperCase()}
                          </label>
                          <Select
                            id="status"
                            name="status"
                            menuPortalTarget={document.body}
                            styles={{
                              ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                              menuPortal: base => ({
                                ...base,
                                zIndex: 9999,
                              })
                            }}
                            width="150px"
                            value={filterStatus}
                            onChange={(selectedOption) => setFilterStatus(selectedOption)}
                            options={statuses}
                            isSearchable={true}
                            placeholder={t('status').toUpperCase()}
                            isDisabled={disableOtherFields}
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", width: "100%", justifyContent: "center", alignItems: "center", margin: "15px 0px 5px 0px" }}>
                      <CustomLoadingButton
                        currentTheme={currentTheme}
                        iconPosition="end"
                        borderRadiusType="both"
                        variant="tint"
                        color="#8BC34A"
                        width={"300px"}
                        endIcon={<PageviewOutlinedIcon />}
                        onClick={handleGetPaymentListing}
                      >
                        {t('run').toUpperCase()}
                      </CustomLoadingButton>
                    </div>
                  </div>
                </ExpandableComponent>
                <div className="filter-wrapper-results">
                  <div className="results-wrapper">
                    <div className="results-wrapper-navbar">
                      <div className="navbar-left">
                        <div className="homeSearch" style={{ width: "90%" }}>
                          <FiSearch className="homeSearchIcon" />
                          <input
                            className="homeSearchInput"
                            type="text"
                            placeholder={t('search')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          {searchTerm.length > 0 && (
                            <MdClose
                              onClick={() => setSearchTerm("")}
                              className="homeSearchClear"
                            />
                          )}
                        </div>
                      </div>
                      <div className="navbar-right">
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px" }}>
                          <CustomLoadingButton
                            currentTheme={currentTheme}
                            iconPosition="end"
                            borderRadiusType="both"
                            variant="tint"
                            color="#0f766e5d"
                            endIcon={<RefreshIcon />}
                            onClick={handleGetPaymentListing}
                          >
                            {t('refresh')}
                          </CustomLoadingButton>
                          <CustomLoadingButton
                            currentTheme={currentTheme}
                            iconPosition="end"
                            borderRadiusType="both"
                            variant="tint"
                            color="#2563EB"
                            tooltip="Run payments reports"
                            endIcon={<IoDocumentTextSharp />}
                            onClick={() => navigate("/reports/ap-payables", { state: { showBack: "Y" } })}>
                            {t('reports')}
                          </CustomLoadingButton>
                          {filteredData?.length > 0 &&
                            <ExportToExcelButton
                              data={filteredData}
                              exportConfig={exportConfig}
                              fileName="Payments.xlsx"
                              currentTheme={currentTheme}
                              borderRadiusType="right"
                            />}
                        </div>
                      </div>
                    </div>
                    <div className="table-content">
                      {slicedPaymentData?.length > 0 &&
                        <div className="table-content">
                          <TableContainer component={Paper} sx={{ border: 'none', boxShadow: 'none' }}
                            className="table-container">
                            <Table sx={{ width: "max-content", border: 'none' }}>
                              <TableBody className="tablebody">
                                {slicedPaymentData?.map((payment, index) => (
                                  <TableRow
                                    key={payment._id}
                                    className="tablerow"
                                    onClick={() => navigate(`/payment/${payment._id}`)}
                                  >
                                    <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                      {page * rowsPerPage + index + 1}
                                    </TableCell>
                                    <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                      <span className="sub-title">{payment?.hd_no} </span>
                                      <span className="sub-title soft xrsmall"> {payment?.hd_mode}</span>
                                    </TableCell>
                                    <TableCell className="table-cell" style={{ flexDirection: "column", width: "auto" }}>
                                      <span className="sub-title">{`${payment?.hd_type === "Normal" ? payment?.client_details_xx?.ent_name : payment?.hd_misc_name}`}</span>
                                      <span className="sub-title soft xrsmall">{`# ${payment?.hd_type === "Normal" ? payment?.client_details_xx?.ent_code : (payment?.hd_misc_idnumber || "-")}`}</span>
                                    </TableCell>
                                    <TableCell className="table-cell right" style={{ maxWidth: "200px" }}>
                                      <span className="sub-title boldsmall"><span className="sub-title xrsmall" style={{ textAlign: "right", fontWeight: "bold", fontSize: "10px", color: "#f44336" }}>[{payment?.hd_cur_code}]</span> : {`${t('paid')} ${numeral(payment?.hd_fc_amount).format("0,0.00")}`}</span>
                                      <span className="sub-title boldsmall">{`${t('allocated')} ${numeral(payment?.allocated_fc_amount_xx).format("0,0.00")}`}</span>
                                    </TableCell>
                                    <TableCell className="table-cell right" style={{ maxWidth: "100px" }}>
                                      <span className="sub-title">{payment?.hd_paying_for}</span>
                                      <span className="sub-title"> {payment?.bizunit_name_xx}</span>
                                    </TableCell>
                                    <TableCell className="table-cell center" style={{ maxWidth: "120px" }}>
                                      <span> {moment(payment?.hd_gl_date).format("DD-MMM-YYYY")}</span>
                                      <span className={`status small ${payment?.hd_status}`}> {payment?.hd_status}</span>
                                    </TableCell>
                                    <TableCell className="table-cell left" style={{ maxWidth: "200px" }}>
                                      <WorkedOnBy name={payment?.updated_by_name_xx} photoUrl={payment?.updated_by_photo_url_xx} time_ago_xx={payment?.time_ago_xx} showTitle={false} />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {filteredData.length > rowsPerPage
                              && <TablePagination
                                rowsPerPageOptions={[5]}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                component="div"
                                count={filteredData.length}
                                page={page}
                                onPageChange={handleChangePage}
                              />}
                          </TableContainer>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </motion.div>}
          </motion.div>
          {isOverlayOpen && (
            <motion.div
              className="overlay-dialog"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 1 } }}
              exit={{ opacity: 0, transition: { duration: 1 } }}
            >
              <div className="overlay-wrapper">
                <div className="overlay-navbar">
                  <span>{t('help')}</span>
                  <CircularButton
                    onClick={() => setIsOverlayOpen(false)}
                    icon={<MdClose />}
                    width={"35px"}
                    height={"35px"} />
                </div>
                <div className="overlay-content">
                  <VideoPlayer videoUrl="https://www.youtube.com/embed/PPCfDe8TfJQ" />
                </div>
                <div className="overlay-actions">
                </div>
              </div>
            </motion.div>
          )}
        </div>
      )}
      {isProcessing && (
        <motion.div
          className="processing-dialog"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 1 } }}
          exit={{ opacity: 0, transition: { duration: 1 } }}
        >
          <div className="processing-wrapper">
            <GridLoader color="#bd97fc"
              style={{
                height: "50px",
                width: "50px",
              }}
            />
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ApPaymentHome;
